import React from "react";
import { PaymentHubAPI } from "../../../providers/PaymentHubAPI";
import { Button } from "../../components/Button";
import { Drawer } from "../../components/Drawer";
import { Input } from "../../components/Input";
import { Loading } from "../../components/Loading";
import { Select } from "../../components/Select";
import { EnvironmentStorage } from "../../tools/EnvironmentStorage";
import { Message } from "../../components/Message";
import { Context } from "pages/Integrations";
import styles from "@styles/integrations.module.scss";
import { Checkbox } from "../../components/Checkbox";
import { EnvironmentsAPI } from "../../../providers/EnvironmentsAPI";
import { PaymentMethodConflictWarning } from "../../components/PaymentMethodConflictWarning";

const SUPPORTED_CATEGORIES = {
    CREDIT: {
        key: "CREDIT",
        label: "Crédito"
    },
    DEBIT: {
        key: "DEBIT",
        label: "Débito"
    },
    VOUCHER: {
        key: "VOUCHER",
        label: "Voucher"
    },
    PIX: {
        key: "PIX",
        label: "PIX"
    },
    GOOGLE_PAY: {
        key: "GOOGLE_PAY",
        label: "Google Pay"
    },
    APPLE_PAY: {
        key: "APPLE_PAY",
        label: "Apple Pay"
    }
}

const CATEGORY_LABELS = Object.values(SUPPORTED_CATEGORIES).reduce((acc, cat) => ({ ...acc, [cat.key]: cat.alias }), {});

export class PagarMeDrawer extends React.Component {

    state = {
        loading: false,
        savingOptions: false,
        accountId: null,
        paymentHubAccountId: null,
        psp_key: null,
        gateway_key: null,
        webhook_url: null,
        webhook_method: null,
        localPaymentMethods: {},
        initialPaymentMethods: {},
        lastActivatedMethod: null
    }

    async setAsyncState(data) {
        return new Promise((resolve) => this.setState(data, resolve));
    }

    async componentDidMount() {
        try {
            await this.setAsyncState({ loading: true });
            const selectedEnvironment = await EnvironmentStorage.getSelectedEnvironment();
            await PaymentHubAPI.initAccount();
            const pagarmeGateway = await PaymentHubAPI.getGatewayConfiguration(selectedEnvironment, `pagarme`);
            const paymentHubAccountId = await PaymentHubAPI.getAccountId(selectedEnvironment);
            const paymenthub = await EnvironmentsAPI.getConfigurationsByAlias(selectedEnvironment, 'paymenthub');
            const currentPaymentMethods = paymenthub?.paymentMethods || {};
            const localPaymentMethods = {};
            Object.keys(SUPPORTED_CATEGORIES).forEach((cat) => {
                localPaymentMethods[cat] = currentPaymentMethods[cat] === "pagarme"
                    ? "pagarme"
                    :  currentPaymentMethods?.[cat];
            });
            this.setState({
                initialPaymentMethods: { ...currentPaymentMethods }
            });
            this.setState({
                accountId: pagarmeGateway.accountId,
                paymentHubAccountId: paymentHubAccountId,
                psp_key: pagarmeGateway.psp_key,
                gateway_key: pagarmeGateway.gateway_key,
                webhook_url: pagarmeGateway.webhook?.url,
                webhook_method: pagarmeGateway.webhook?.method,
                localPaymentMethods: localPaymentMethods
            })
            await this.setAsyncState({ loading: false });
        } catch (err) {
            await this.setAsyncState({ loading: false });
        }
    }

    handlePaymentMethodCheckbox = (cat, checked) => {
        this.setState((prevState) => {
            const wasChecked = prevState.localPaymentMethods[cat] === "pagarme";
            const willBeChecked = checked;
            let lastActivatedMethod = null;
            if (!wasChecked && willBeChecked) {
                lastActivatedMethod = cat;
            }
            return {
                localPaymentMethods: {
                    ...prevState.localPaymentMethods,
                    [cat]: checked ? "pagarme" : ""
                },
                lastActivatedMethod
            };
        });
    }

    async onSave() {
        try {
            const { onSave } = this.props;
            const { psp_key, gateway_key, webhook_url, webhook_method, localPaymentMethods, accountId } = this.state;
            await this.setAsyncState({ savingOptions: true });
            const selectedEnvironment = await EnvironmentStorage.getSelectedEnvironment();
            const paymenthub = await EnvironmentsAPI.getConfigurationsByAlias(selectedEnvironment, 'paymenthub');
            const currentPaymentMethods = paymenthub?.paymentMethods || {};
            const updatedPaymentMethods = { ...currentPaymentMethods };


            Object.keys(localPaymentMethods).forEach((cat) => {
                if (cat === 'PIX') {
                    if (localPaymentMethods["PIX"] === "pagarme") {
                        updatedPaymentMethods["PIX"] = "pagarme";
                        updatedPaymentMethods["MONEY"] = "pagarme";
                    }  else {
                        updatedPaymentMethods["PIX"] = updatedPaymentMethods?.PIX === 'pagarme'
                            ?  localPaymentMethods["PIX"] 
                            :  updatedPaymentMethods["PIX"];

                        updatedPaymentMethods["MONEY"] = updatedPaymentMethods?.MONEY === 'pagarme'
                            ?  localPaymentMethods["MONEY"] 
                            :  updatedPaymentMethods["MONEY"];
                    }
                } else {
                    updatedPaymentMethods[cat] = localPaymentMethods[cat] || null;
                }
            });

            paymenthub.paymentMethods = updatedPaymentMethods;
            paymenthub.gateways = {
                ...(paymenthub.gateways || {}),
                pagarme: {
                    accountId
                }
            };

            await EnvironmentsAPI.putConfigurationsByAlias(selectedEnvironment, 'paymenthub', paymenthub);

            const body = {
                accountId: accountId,
                psp_key: psp_key,
                gateway_key: gateway_key,
                webhook: {
                    url: webhook_url,
                    method: webhook_method
                },
                paymentMethods: updatedPaymentMethods
            };

            const response = await PaymentHubAPI.setGatewayConfiguration(selectedEnvironment, `pagarme`, body);

            await this.setAsyncState({ savingOptions: false });
            if (response.getErrors([]).length === 0) {
                onSave && onSave();
            } else {
                alert({ text: `Não foi possível salvar as credenciais: ${response.getErrors()[0]}` });
            }
        } catch (e) {
            await this.setAsyncState({ savingOptions: false });
        }
    }

    render() {
        const { visible, onClose } = this.props;
        const { psp_key, gateway_key, webhook_url, webhook_method, savingOptions, loading, accountId, paymentHubAccountId, localPaymentMethods } = this.state;

        return (
            <Drawer
                visible={visible}
                onClose={onClose}
                closeInFade
                title={"Integração - Pagar.me"}
                footer={
                    <Button
                        design="primary"
                        className="w-100"
                        onClick={this.onSave.bind(this)}
                        loading={savingOptions}
                        disabled={loading || savingOptions}
                    >
                        Salvar alterações
                    </Button>
                }
            >
                <Context />

                {loading ? (
                    <div className="d-flex justify-center align-center h-100">
                        <Loading fullScreen />
                    </div>
                ) : (
                    <div className={styles.scrollContainer}>
                        <Input
                            design="two"
                            className="mb-20"
                            floatLabel="ID da conta"
                            placeholder="acc_xxxxxxxxxx"
                            value={accountId}
                            onChange={(e) => this.setState({ accountId: e.target.value })}
                        />

                        <Input
                            design="two"
                            className="mb-20"
                            floatLabel="Chave PSP"
                            value={psp_key}
                            onChange={(e) => this.setState({ psp_key: e.target.value })}
                        />
                        <Input
                            design="two"
                            className="mb-20"
                            floatLabel="Chave Gateway"
                            value={gateway_key}
                            onChange={(e) => this.setState({ gateway_key: e.target.value })}
                        />
                        <Input
                            design="two"
                            className="mb-20"
                            floatLabel="URL do webhook"
                            value={webhook_url}
                            onChange={(e) => this.setState({ webhook_url: e.target.value })}
                        />
                        <Select
                            design="two"
                            label="Método da requisição"
                            onChange={(e) => this.setState({ webhook_method: e.target.value })}
                            className="flex-1"
                            value={webhook_method}
                            options={[
                                { label: "GET", value: "GET", selected: webhook_method == "GET" },
                                { label: "POST", value: "POST", selected: webhook_method == "POST" },
                                { label: "PUT", value: "PUT", selected: webhook_method == "PUT" },
                                { label: "PATCH", value: "PATCH", selected: webhook_method == "PATCH" },
                            ]}
                        />
                        <Message className="mt-20">
                            <p>
                                Configure em seu gateway de pagamento o webhook com a url abaixo 
                                e os eventos <b>charge.pending</b>, <b>charge.paid</b>, <b>charge.payment_failed</b> e <b>charge.antifraud_reproved</b> marcados
                            </p>
                            <br />
                            <p>
                                https://api.food.olga.tech/payment-hub/webhook/distributed/{paymentHubAccountId}/pagarme
                            </p>
                        </Message>
                        <p className={styles.subtitle}>
                            Métodos de pagamento disponíveis
                        </p>
                        <PaymentMethodConflictWarning
                            supportedCategories={Object.keys(SUPPORTED_CATEGORIES)}
                            localPaymentMethods={localPaymentMethods}
                            initialPaymentMethods={this.state.initialPaymentMethods}
                            lastActivatedMethod={this.state.lastActivatedMethod}
                            categoryLabels={CATEGORY_LABELS}
                            integrationName="Pagar.me"
                            gatewayName="pagarme"
                        />
                        {Object.values(SUPPORTED_CATEGORIES).map((cat) => (
                            <div key={cat.key} className={styles.paymentMethodRow}>
                                <Checkbox
                                    checked={localPaymentMethods[cat.key] === "pagarme"}
                                    onChange={(checked) => this.handlePaymentMethodCheckbox(cat.key, checked)}
                                    label={cat.label}
                                    name={`pagarme-method-${cat.key}`}
                                />
                            </div>
                        ))}
                    </div>
                )}
            </Drawer>
        );
    }
}